import type { MenuItemProps } from '@base-ui/react';

import { type ComponentPropsWithRef, use } from 'react';

import type { IconName } from '../Icon/Icon';

import { Button } from '../Button/Button';
import { defaultEmphasis } from '../Button/Button.utils';
import { ExternalLinkButton } from '../Button/ExternalLinkButton';
import { LinkButton } from '../Button/LinkButton';
import { Menu } from '../Menu/Menu';
import { FloatingAction, FloatingExternalLinkAction, FloatingLinkAction } from './FloatingAction';
import { FloatingMenuContext } from './HeaderActions.context';
import { useFloatingEmphasis } from './HeaderActions.utils';

/** Since a header action can become a floating action, it needs an icon and an accessible name. */
type SharedHeaderActionProps = { icon: IconName } & ({ children: string } | { 'aria-label': string; children?: never });

// Header Action

type HeaderActionProps = ComponentPropsWithRef<typeof Button> & SharedHeaderActionProps;

export const HeaderAction = ({ ...delegated }: HeaderActionProps) => {
  const isFloatingMenu = use(FloatingMenuContext);
  const fabEmphasis = useFloatingEmphasis(delegated.emphasis ?? defaultEmphasis(delegated));

  if (isFloatingMenu) {
    const { children, className, emphasis, iconPlacement, loading, style, ...itemDelegated } = delegated;

    return (
      <Menu.Item {...(itemDelegated as MenuItemProps)} disabled={loading || itemDelegated.disabled}>
        {children ?? itemDelegated['aria-label']}
      </Menu.Item>
    );
  }

  if (fabEmphasis === null) return <Button {...delegated} />;

  const { children, className, style, ...fabDelegated } = delegated;

  return <FloatingAction {...fabDelegated} aria-label={fabDelegated['aria-label'] ?? children ?? ''} emphasis={fabEmphasis} />;
};

// Header Link

type HeaderLinkProps = ComponentPropsWithRef<typeof LinkButton> & SharedHeaderActionProps;

export const HeaderLinkAction = ({ ...delegated }: HeaderLinkProps) => {
  const isFloatingMenu = use(FloatingMenuContext);
  const fabEmphasis = useFloatingEmphasis(delegated.emphasis ?? defaultEmphasis(delegated));

  if (isFloatingMenu) {
    const { children, className, emphasis, iconPlacement, style, ...itemDelegated } = delegated;

    return <Menu.LinkItem {...itemDelegated}>{children ?? itemDelegated['aria-label']}</Menu.LinkItem>;
  }

  if (fabEmphasis === null) return <LinkButton {...delegated} />;

  const { children, className, style, ...fabDelegated } = delegated;

  return <FloatingLinkAction {...fabDelegated} aria-label={fabDelegated['aria-label'] ?? children ?? ''} emphasis={fabEmphasis} />;
};

// Header External Link

type HeaderExternalLinkProps = ComponentPropsWithRef<typeof ExternalLinkButton> & SharedHeaderActionProps;

export const HeaderExternalLinkAction = ({ ...delegated }: HeaderExternalLinkProps) => {
  const isFloatingMenu = use(FloatingMenuContext);
  const fabEmphasis = useFloatingEmphasis(delegated.emphasis ?? defaultEmphasis(delegated));

  if (isFloatingMenu) {
    const { children, className, emphasis, iconPlacement, style, ...itemDelegated } = delegated;

    return <Menu.ExternalLinkItem {...itemDelegated}>{children ?? itemDelegated['aria-label']}</Menu.ExternalLinkItem>;
  }

  if (fabEmphasis === null) return <ExternalLinkButton {...delegated} />;

  const { children, className, style, ...fabDelegated } = delegated;

  return <FloatingExternalLinkAction {...fabDelegated} aria-label={fabDelegated['aria-label'] ?? children ?? ''} emphasis={fabEmphasis} />;
};
